import type { SettingComponentConfig } from './settingComponentRegistry'
import { buildSettingOptionsByDoc } from './settingComponentRegistry'

/**
 * Preview `data-comp` → ThemeBuilder Form deep-link — **source of truth**.
 *
 * Contract: `{FormSection}.{CollapsibleList.Item id}.{settingLeaf}`
 * - `action` = CollapsibleList.Item `id` (L2 open)
 * - `scrollTo` = DOM id / theme.settings key to highlight (L3)
 * - `needsDataComp` = false nếu BACKEND/STYLES (không cần node Liquid)
 * - `parentDataComp` = nested share parent node khi thiếu node riêng
 *
 * Doc applicability: `SettingOptionDocs` trong `settingComponentRegistry.ts`.
 *
 * Thêm setting mới:
 * 1. Entry tại đây (+ parentDataComp / needsDataComp nếu cần)
 * 2. `SettingOptionDocs[scrollTo] = [...]`
 * → test:data-comp tự cover.
 */
export const SettingComponentMap: Record<string, SettingComponentConfig> = {
  /**
   * Element: BrandStyle
   */
  'BrandStyle.logo.is_logo': {
    action: 'logo',
    scrollTo: 'is_logo',
  },
  /**
   * Element: Overview
   */
  'Overview.documentTitle.heading': {
    action: 'document-title',
    scrollTo: 'is_show_heading_overview',
  },
  'Overview.orderName.orderNumber': {
    action: 'order-name',
    scrollTo: 'is_number',
  },
  'Overview.orderName.customNumber': {
    action: 'order-name',
    scrollTo: 'is_custom_number',
    parentDataComp: [
      'Overview.orderName.orderNumber',
      'Overview.refund.orderNumber',
    ],
  },
  'Overview.orderName.shopifyOrderNumber': {
    action: 'order-name',
    scrollTo: 'show_shopify_order_number',
    parentDataComp: [
      'Overview.orderName.orderNumber',
      'Overview.refund.orderNumber',
    ],
  },
  'Overview.refund.refundNumber': {
    action: 'refund-number',
    scrollTo: 'is_refund_no',
  },
  'Overview.refund.orderNumber': {
    action: 'order-name',
    scrollTo: 'is_number',
    parentDataComp: 'Overview.orderName.orderNumber',
  },
  'Overview.date.issueDate': {
    action: 'issue-date',
    scrollTo: 'is_issue_date',
  },
  'Overview.date.refundDate': {
    action: 'refund-date',
    scrollTo: 'is_refund_date',
  },
  'Overview.date.deliveredDate': {
    action: 'delivered-date',
    scrollTo: 'show_delivered_date',
  },
  'Overview.date.dueDate': {
    action: 'due-date',
    scrollTo: 'show_due_date',
  },
  'Overview.date.estimatedDate': {
    action: 'estimated-date',
    scrollTo: 'show_estimated_date',
  },
  'Overview.payment.paymentMethod': {
    action: 'payment-method',
    scrollTo: 'is_payment_method',
  },
  'Overview.payment.shippingMethod': {
    action: 'shipping-method',
    scrollTo: 'is_shipping_method',
  },
  'Overview.barcode.barcode': {
    action: 'barcode',
    scrollTo: 'is_barcode',
  },
  'Overview.barcode.barcodeNumber': {
    action: 'barcode',
    scrollTo: 'is_barcode_number',
    parentDataComp: 'Overview.barcode.barcode',
  },
  /**
   * Form-only (DocumentSettings). Date cells use Overview.date.* on Liquid.
   */
  'DocumentSettings.dateFormat.dateFormat': {
    action: 'date-format',
    scrollTo: 'date_format',
  },
  'Overview.extraOptions.paymentTerms': {
    action: 'payment-terms',
    scrollTo: 'show_payment_terms',
  },
  'Overview.extraOptions.poNumber': {
    action: 'po-number',
    scrollTo: 'show_po_number',
  },
  'Overview.extraOptions.tags': {
    action: 'order-tags',
    scrollTo: 'show_tags',
  },
  'Overview.extraOptions.orderMetafields': {
    action: 'extra-options',
    scrollTo: 'show_order_metafields',
  },
  'Overview.extraOptions.customInfo': {
    action: 'extra-options',
    scrollTo: 'show_invoice_extra_options',
  },
  /**
   * Element: Client (shipping address)
   */
  'Client.heading.heading': {
    action: 'heading',
    scrollTo: 'is_shipping_heading',
  },
  'Client.displayOptions.shippingName': {
    action: 'shipping-name',
    scrollTo: 'is_shipping_name',
  },
  'Client.displayOptions.shippingCompany': {
    action: 'shipping-company',
    scrollTo: 'is_shipping_company',
  },
  'Client.displayOptions.shippingCompanyId': {
    action: 'shipping-company-id',
    scrollTo: 'is_shipping_company_id',
  },
  'Client.displayOptions.shippingAddress': {
    action: 'shipping-address',
    scrollTo: 'is_shipping_address',
  },
  'Client.displayOptions.shippingApartment': {
    action: 'shipping-apartment',
    scrollTo: 'is_shipping_apartment',
    parentDataComp: 'Client.displayOptions.shippingAddress',
  },
  'Client.displayOptions.shippingCity': {
    action: 'shipping-city',
    scrollTo: 'is_shipping_city',
    parentDataComp: 'Client.displayOptions.shippingAddress',
  },
  'Client.displayOptions.shippingCountry': {
    action: 'shipping-country',
    scrollTo: 'is_shipping_country',
    parentDataComp: 'Client.displayOptions.shippingAddress',
  },
  'Client.displayOptions.shippingZip': {
    action: 'shipping-zip',
    scrollTo: 'is_shipping_zip',
    parentDataComp: 'Client.displayOptions.shippingAddress',
  },
  'Client.displayOptions.shippingPhone': {
    action: 'shipping-phone',
    scrollTo: 'is_shipping_phone',
  },
  'Client.displayOptions.shippingEmail': {
    action: 'shipping-email',
    scrollTo: 'is_shipping_email',
  },
  'Client.displayOptions.customerMetafields': {
    action: 'customer-metafields',
    scrollTo: 'show_customer_metafields',
  },
  'Client.displayOptions.shippingLocationTaxId': {
    action: 'shipping-location-tax-id',
    scrollTo: 'is_shipping_location_tax_id',
  },
  /**
   * Element: Billing
   */
  'Billing.heading.heading': {
    action: 'heading',
    scrollTo: 'is_billing_heading',
  },
  'Billing.displayOptions.billingName': {
    action: 'billing-name',
    scrollTo: 'is_billing_name',
  },
  'Billing.displayOptions.billingCompany': {
    action: 'billing-company',
    scrollTo: 'is_billing_company',
  },
  'Billing.displayOptions.billingCompanyId': {
    action: 'billing-company-id',
    scrollTo: 'is_billing_company_id',
  },
  'Billing.displayOptions.billingAddress': {
    action: 'billing-address',
    scrollTo: 'is_billing_address',
  },
  'Billing.displayOptions.billingApartment': {
    action: 'billing-apartment',
    scrollTo: 'is_billing_apartment',
    parentDataComp: 'Billing.displayOptions.billingAddress',
  },
  'Billing.displayOptions.billingCity': {
    action: 'billing-city',
    scrollTo: 'is_billing_city',
    parentDataComp: 'Billing.displayOptions.billingAddress',
  },
  'Billing.displayOptions.billingCountry': {
    action: 'billing-country',
    scrollTo: 'is_billing_country',
    parentDataComp: 'Billing.displayOptions.billingAddress',
  },
  'Billing.displayOptions.billingZip': {
    action: 'billing-zip',
    scrollTo: 'is_billing_zip',
    parentDataComp: 'Billing.displayOptions.billingAddress',
  },
  'Billing.displayOptions.billingPhone': {
    action: 'billing-phone',
    scrollTo: 'is_billing_phone',
  },
  'Billing.displayOptions.billingEmail': {
    action: 'billing-email',
    scrollTo: 'is_billing_email',
  },
  'Billing.displayOptions.billingLocationTaxId': {
    action: 'billing-location-tax-id',
    scrollTo: 'is_billing_location_tax_id',
  },
  'Billing.displayOptions.taxNumber': {
    action: 'tax-number',
    scrollTo: 'is_tax_number',
  },
  /**
   * Element: Supplier (shop)
   */
  'Supplier.heading.heading': {
    action: 'heading',
    scrollTo: 'is_supplier_heading',
  },
  'Supplier.displayOptions.shopName': {
    action: 'shop-name',
    scrollTo: 'is_supplier_name',
  },
  'Supplier.displayOptions.shopAddress': {
    action: 'street-address',
    scrollTo: 'is_supplier_address',
  },
  'Supplier.displayOptions.shopApartment': {
    action: 'apartment',
    scrollTo: 'is_supplier_apartment',
    parentDataComp: 'Supplier.displayOptions.shopAddress',
  },
  'Supplier.displayOptions.shopCity': {
    action: 'city',
    scrollTo: 'supplier_show_city',
    parentDataComp: 'Supplier.displayOptions.shopAddress',
  },
  'Supplier.displayOptions.shopCountry': {
    action: 'country',
    scrollTo: 'supplier_show_country',
    parentDataComp: 'Supplier.displayOptions.shopAddress',
  },
  'Supplier.displayOptions.shopVatNumber': {
    action: 'vat-number',
    scrollTo: 'is_supplier_vat_number',
  },
  'Supplier.displayOptions.shopRegisteredNumber': {
    action: 'registered-number',
    scrollTo: 'is_supplier_registered_number',
  },
  'Supplier.displayOptions.shopPhone': {
    action: 'phone',
    scrollTo: 'is_supplier_phone',
  },
  'Supplier.displayOptions.shopEmail': {
    action: 'email',
    scrollTo: 'is_supplier_email',
  },
  'Supplier.customInfo.customInfo': {
    action: 'custom-info',
    scrollTo: 'show_supplier_extra',
  },
  /**
   * Element: Line Item
   */
  'LineItem.column.checkbox': {
    action: 'item-checkbox',
    scrollTo: 'is_item_checkbox',
  },
  'LineItem.column.image': {
    action: 'item-image',
    scrollTo: 'is_item_thumbnail',
  },
  'LineItem.column.productTitle': {
    action: 'product-title',
    scrollTo: 'show_product_title',
  },
  'LineItem.bundleComponents': {
    action: 'item-column',
    scrollTo: 'show_bundle',
  },
  'LineItem.column.sku': {
    action: 'item-sku',
    scrollTo: 'is_item_sku',
  },
  'LineItem.column.barcode': {
    action: 'item-barcode-value',
    scrollTo: 'show_item_barcode',
  },
  'LineItem.column.vendor': {
    action: 'item-vendor',
    scrollTo: 'is_item_vendor',
  },
  'LineItem.column.qty': {
    action: 'item-qty',
    scrollTo: 'is_item_quantity',
  },
  'LineItem.column.hsCode': {
    action: 'item-hs-code',
    scrollTo: 'show_hs_code',
  },
  'LineItem.column.itemMetafields': {
    action: 'item-metafields',
    scrollTo: 'show_item_metafields',
  },
  'LineItem.barcode.barcode': {
    action: 'barcode-row',
    scrollTo: 'is_item_barcode',
  },
  'LineItem.barcode.showValue': {
    action: 'barcode-row',
    scrollTo: 'is_show_barcode_value',
    parentDataComp: 'LineItem.barcode.barcode',
  },
  'LineItem.description.itemDescription': {
    action: 'desc-column',
    scrollTo: 'show_item_description',
  },
  'LineItem.description.prodWeight': {
    action: 'desc-column',
    scrollTo: 'show_item_product_weight',
  },
  'LineItem.description.prodWeightUnit': {
    action: 'desc-column',
    scrollTo: 'is_item_weight_unit',
    needsDataComp: false,
    parentDataComp: 'LineItem.description.prodWeight',
  },
  'LineItem.description.prodType': {
    action: 'desc-column',
    scrollTo: 'show_item_product_type',
  },
  'LineItem.description.prodCategory': {
    action: 'desc-column',
    scrollTo: 'show_item_product_category',
  },
  'LineItem.description.qtyInStock': {
    action: 'desc-column',
    scrollTo: 'show_in_stock',
  },
  'LineItem.description.customAttribute': {
    action: 'desc-column',
    scrollTo: 'is_item_properties',
  },
  'LineItem.description.variantMetafields': {
    action: 'desc-column',
    scrollTo: 'show_variant_metafields',
  },
  'LineItem.price.unitPrice': {
    action: 'price',
    scrollTo: 'is_item_price',
  },
  'LineItem.price.unitPriceExclTax': {
    action: 'price',
    scrollTo: 'is_item_unit_price_excl_tax',
    needsDataComp: false,
  },
  'LineItem.price.priceAfterDiscount': {
    action: 'price',
    scrollTo: 'is_price_after_discount',
    parentDataComp: 'LineItem.price.unitPrice',
  },
  'LineItem.price.comparePrice': {
    action: 'compare-at-price',
    scrollTo: 'is_item_compare_at_price',
  },
  'LineItem.price.discount': {
    action: 'item-discount',
    scrollTo: 'is_item_discount',
  },
  'LineItem.price.taxAmount': {
    action: 'item-tax',
    scrollTo: 'is_item_tax',
  },
  'LineItem.price.taxName': {
    action: 'item-tax',
    scrollTo: 'is_show_tax_name',
  },
  'LineItem.price.taxPercent': {
    action: 'item-tax',
    scrollTo: 'is_show_tax_percent',
  },
  'LineItem.price.totalPrice': {
    action: 'item-subtotal',
    scrollTo: 'is_item_total',
  },
  'LineItem.price.totalExclTax': {
    action: 'item-subtotal',
    scrollTo: 'is_item_subtotal_ex_tax',
  },
  'LineItem.price.subtotalIncDiscount': {
    action: 'item-subtotal',
    scrollTo: 'is_item_subtotal_inc_discount',
    needsDataComp: false,
  },
  /** Packing slip — financial status (LineItemPrices L2). */
  'LineItem.price.footerStatus': {
    action: 'financial-status',
    scrollTo: 'is_footer_status',
  },
  'LineItem.total.totalWeight': {
    action: 'total-weight',
    scrollTo: 'show_total_weight',
  },
  'LineItem.total.subTotal': {
    action: 'subtotal',
    scrollTo: 'is_subtotal',
  },
  'LineItem.total.subTotalExclTax': {
    action: 'subtotal',
    scrollTo: 'is_subtotal_ex_tax',
  },
  'LineItem.total.subTotalIncDiscount': {
    action: 'subtotal',
    scrollTo: 'is_subtotal_inc_discount',
    needsDataComp: false,
  },
  'LineItem.total.totalDiscount': {
    action: 'total-discount',
    scrollTo: 'is_discount',
  },
  'LineItem.total.discountCode': {
    action: 'total-discount',
    scrollTo: 'show_discount_code',
    parentDataComp: 'LineItem.total.totalDiscount',
  },
  'LineItem.total.totalShipping': {
    action: 'order-shipping',
    scrollTo: 'is_shipping',
  },
  'LineItem.total.netShipping': {
    action: 'order-shipping',
    scrollTo: 'is_net_shipping',
  },
  'LineItem.total.totalShippingRefund': {
    action: 'refund-shipping',
    scrollTo: 'is_total_shipping',
  },
  'LineItem.total.shippingMethod': {
    action: 'order-shipping',
    scrollTo: 'show_shipping_method',
    parentDataComp: 'LineItem.total.totalShipping',
  },
  'LineItem.total.duties': {
    action: 'duties',
    scrollTo: 'show_duties',
  },
  'LineItem.total.totalTax': {
    action: 'total-tax',
    scrollTo: 'is_tax',
  },
  'LineItem.total.taxName': {
    action: 'total-tax',
    scrollTo: 'show_tax_line',
    parentDataComp: 'LineItem.total.totalTax',
  },
  'LineItem.total.totalRefund': {
    action: 'total-refunded',
    scrollTo: 'is_total_refund',
  },
  'LineItem.total.totalPaid': {
    action: 'total-paid',
    scrollTo: 'is_total_paid',
  },
  'LineItem.total.totalOutstanding': {
    action: 'total-outstanding',
    scrollTo: 'is_total_outstanding',
  },
  'LineItem.total.giftCardsTotal': {
    action: 'gift-cards',
    scrollTo: 'show_used_gift_cards',
  },
  'LineItem.total.giftCardCode': {
    action: 'gift-cards',
    scrollTo: 'show_gift_card_code',
    parentDataComp: 'LineItem.total.giftCardsTotal',
  },
  'LineItem.total.totalPrice': {
    action: 'grand-total',
    scrollTo: 'is_total',
  },
  'LineItem.total.shippingTax': {
    action: 'refund-shipping',
    scrollTo: 'is_total_shipping_tax',
  },
  'LineItem.total.qty': {
    action: 'total-qty',
    scrollTo: 'show_total_qty',
  },
  'LineItem.note.note': {
    action: 'note',
    scrollTo: 'show_order_note',
  },
  'LineItem.note.tableNote': {
    action: 'table-note',
    scrollTo: 'items_note',
  },
  /**
   * Element: Footer
   * Canonical Liquid keys: Footer.displayOptions.*
   * Legacy aliases: Footer.options.* (same scrollTo)
   */
  'Footer.cartAttribute.cartAttribute': {
    action: 'attribute-details',
    scrollTo: 'is_item_properties_order',
  },
  'Footer.displayOptions.qrCode': {
    action: 'qr-code',
    scrollTo: 'show_order_status_url',
  },
  'Footer.displayOptions.link': {
    action: 'online-version',
    scrollTo: 'is_online_version',
  },
  /** @deprecated Prefer Footer.displayOptions.qrCode */
  'Footer.options.show_order_status_url': {
    action: 'qr-code',
    scrollTo: 'show_order_status_url',
  },
  /** @deprecated Prefer Footer.displayOptions.link */
  'Footer.options.is_online_version': {
    action: 'online-version',
    scrollTo: 'is_online_version',
  },
  'Footer.thankyouNote.note': {
    action: 'thank-note',
    scrollTo: 'show_thank_you_note',
  },
  'Footer.footerNote.note': {
    action: 'footer-note',
    scrollTo: 'footer_note',
  },
  'Footer.social.website': {
    action: 'social-website',
    scrollTo: 'is_footer_website',
  },
  'Footer.social.facebook': {
    action: 'social-facebook',
    scrollTo: 'is_footer_facebook',
  },
  'Footer.social.twitter': {
    action: 'social-twitter',
    scrollTo: 'is_footer_twitter',
  },
  'Footer.social.pinterest': {
    action: 'social-pinterest',
    scrollTo: 'is_footer_pinterest',
  },
  'Footer.social.instagram': {
    action: 'social-instagram',
    scrollTo: 'is_footer_instagram',
  },
  'Footer.social.youtube': {
    action: 'social-youtube',
    scrollTo: 'is_footer_youtube',
  },
  'Footer.term.customInfo': {
    action: 'term',
    scrollTo: 'show_footer_term',
  },
  /**
   * Element: ThemePersonalize (theme-specific L1, e.g. Swanepoel)
   * lineItemAppearance.* styles LineItem.column.image — Form-only highlight.
   */
  'ThemePersonalize.headerThankYou.note': {
    action: 'header-thank-you-note',
    scrollTo: 'header_thank_you_note',
  },
  'ThemePersonalize.signature.signature': {
    action: 'signature',
    scrollTo: 'show_signature',
  },
  'ThemePersonalize.footerBg.image': {
    action: 'footer-bg-image',
    scrollTo: 'show_footer_logo',
  },
  'ThemePersonalize.lineItemAppearance.background': {
    action: 'line-item-appearance',
    scrollTo: 'custom_line_item_bg',
  },
  'ThemePersonalize.lineItemAppearance.rounder': {
    action: 'line-item-appearance',
    scrollTo: 'item_image_rounder',
  },
  'ThemePersonalize.checkoutUrl.link': {
    action: 'checkout-url',
    scrollTo: 'show_checkout_url',
  },
  /**
   * Element: Location
   */
  'Location.heading.heading': {
    action: 'heading',
    scrollTo: 'show_fulfill_heading',
  },
  'Location.displayOptions.fulfillmentName': {
    action: 'fulfill-name',
    scrollTo: 'show_fulfill_name',
  },
  'Location.displayOptions.company': {
    action: 'fulfill-company',
    scrollTo: 'show_fulfill_company',
    parentDataComp: 'Location.displayOptions.fulfillmentName',
  },
  'Location.displayOptions.address': {
    action: 'fulfill-address',
    scrollTo: 'show_fulfill_address',
  },
  'Location.displayOptions.apartment': {
    action: 'fulfill-apartment',
    scrollTo: 'show_fulfill_apartment',
    parentDataComp: 'Location.displayOptions.address',
  },
  'Location.displayOptions.city': {
    action: 'fulfill-city',
    scrollTo: 'show_fulfill_city',
    parentDataComp: 'Location.displayOptions.address',
  },
  'Location.displayOptions.country': {
    action: 'fulfill-country',
    scrollTo: 'show_fulfill_country',
    parentDataComp: 'Location.displayOptions.address',
  },
  'Location.displayOptions.zipCode': {
    action: 'fulfill-zip',
    scrollTo: 'show_fulfill_zip',
  },
  'Location.displayOptions.phone': {
    action: 'fulfill-phone',
    scrollTo: 'show_fulfill_phone',
  },
  'Location.displayOptions.email': {
    action: 'fulfill-email',
    scrollTo: 'show_fulfill_email',
  },
  /**
   * Element: TrackingInfo
   */
  'TrackingInfo.heading.heading': {
    action: 'heading',
    scrollTo: 'show_tracking_heading',
  },
  'TrackingInfo.displayOptions.trackingCompany': {
    action: 'tracking-company',
    scrollTo: 'show_tracking_company',
  },
  'TrackingInfo.displayOptions.trackingNumber': {
    action: 'tracking-number',
    scrollTo: 'show_tracking_number',
  },
  'TrackingInfo.displayOptions.trackingUrl': {
    action: 'tracking-url',
    scrollTo: 'show_tracking_url',
  },
  /**
   * Element: TimelineInfo (packing slip / timeline docs)
   */
  TimelineInfo: {
    action: 'heading',
    scrollTo: 'show_timeline',
  },
  'TimelineInfo.heading.heading': {
    action: 'heading',
    scrollTo: 'show_timeline_heading',
  },
  'TimelineInfo.displayOptions.separatePage': {
    action: 'separate-page',
    scrollTo: 'timeline_separate_page',
  },
  'TimelineInfo.events.message': {
    action: 'heading',
    scrollTo: 'show_timeline',
  },
  'TimelineInfo.events.activity': {
    action: 'heading',
    scrollTo: 'show_timeline',
  },
  'TimelineInfo.events.date': {
    action: 'event-date',
    scrollTo: 'show_timeline_event_date',
  },
  'TimelineInfo.events.location': {
    action: 'event-location',
    scrollTo: 'show_timeline_event_location',
  },
  'TimelineInfo.events.*': {
    action: 'event-date',
    scrollTo: 'show_timeline_event_date',
  },
  /**
   * Default fallback
   */
  '*': {
    action: '',
    scrollTo: '',
  },
}

/** Alias tương thích — prefer SettingComponentMap + SettingOptionDocs. */
export const SETTING_OPTIONS_BY_DOC = buildSettingOptionsByDoc()
